import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { auditStatusConfigKeys } from "@/lib/query";

export interface AuditStatusConfig {
  id: string;
  key: string;
  label: string;
  color: string;
  sortOrder: number;
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
}

export function useAuditStatusConfigs() {
  return useQuery({
    queryKey: auditStatusConfigKeys.all,
    queryFn: async (): Promise<AuditStatusConfig[]> => {
      const res = await fetch("/api/audit-status-configs");
      if (!res.ok) throw new Error("Failed to fetch audit statuses");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useCreateAuditStatusConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { key: string; label: string; color: string }) => {
      const res = await fetch("/api/audit-status-configs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? "Failed to create status");
      return json.data as AuditStatusConfig;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditStatusConfigKeys.all });
      toast.success("Status created");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to create status");
    },
  });
}

export function useUpdateAuditStatusConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { id: string; label?: string; color?: string }) => {
      const { id, ...patch } = data;
      const res = await fetch(`/api/audit-status-configs/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? "Failed to update status");
      return json.data as AuditStatusConfig;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditStatusConfigKeys.all });
      toast.success("Status updated");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to update status");
    },
  });
}

export function useDeleteAuditStatusConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/audit-status-configs/${id}`, {
        method: "DELETE",
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? "Failed to delete status");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: auditStatusConfigKeys.all });
      toast.success("Status deleted");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to delete status");
    },
  });
}
